import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Alert, 
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Chip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { 
  Search as SearchIcon, 
  Clear as ClearIcon, 
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import AddNotificationDialog from '../components/notifications/AddNotificationDialog';
import EditNotificationDialog from '../components/notifications/EditNotificationDialog';
import { getNotifications, deleteNotification, updateNotificationStatus, Notification } from '../services/notificationService';

interface NotificationRow extends Notification {}

export const NotificationsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [filteredRows, setFilteredRows] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'id', sort: 'desc' },
  ]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState<Notification | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [notificationToEdit, setNotificationToEdit] = useState<Notification | null>(null);
  
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await getNotifications();
      setRows(data);
      setFilteredRows(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Error al cargar las notificaciones. Por favor, intente nuevamente.');
      enqueueSnackbar('Error al cargar las notificaciones', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Filter rows based on search text
  useEffect(() => {
    if (searchText === '') {
      setFilteredRows(rows);
    } else {
      const filtered = rows.filter(row => 
        Object.values(row).some(
          value => 
            value && 
            value.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      );
      setFilteredRows(filtered);
    }
  }, [searchText, rows]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchText('');
  };

  const handleStatusChange = async (id: number, newStatus: 'activo' | 'inactivo') => {
    try {
      await updateNotificationStatus(id, newStatus);
      await fetchNotifications();
      enqueueSnackbar('Estado de la notificación actualizado', { variant: 'success' });
    } catch (error) {
      console.error('Error updating notification status:', error);
      enqueueSnackbar('Error al actualizar el estado de la notificación', { variant: 'error' });
    }
  };

  const handleDeleteClick = (notification: Notification) => {
    setNotificationToDelete(notification);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!notificationToDelete) return;
    
    try {
      setIsDeleting(true);
      await deleteNotification(notificationToDelete.id);
      await fetchNotifications();
      enqueueSnackbar('Notificación eliminada correctamente', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting notification:', error);
      enqueueSnackbar('Error al eliminar la notificación', { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setNotificationToDelete(null);
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setNotificationToDelete(null);
  };

  const columns: GridColDef[] = [
    { 
      field: 'img', 
      headerName: 'Imagen', 
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #e0e0e0',
          }}
        >
          {params.value ? (
            <img 
              src={params.value} 
              alt={params.row.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '';
                target.parentElement!.style.padding = '8px';
              }}
            />
          ) : (
            <NotificationsIcon color="disabled" fontSize="large" />
          )}
        </Box>
      ),
      sortable: false,
      filterable: false,
    },
    { 
      field: 'title', 
      headerName: 'Título', 
      width: 200,
      cellClassName: 'left-aligned-cell',
    },
    { 
      field: 'description', 
      headerName: 'Descripción', 
      flex: 1,
      minWidth: 300,
      cellClassName: 'left-aligned-cell',
    },
    {
      field: 'status',
      headerName: 'ESTADO',
      width: 150,
      headerAlign: 'left',
      align: 'left',
      cellClassName: 'left-aligned-cell',
      renderCell: (params) => {
        const isActive = params.value === 'activo';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={isActive}
                onChange={(e) => handleStatusChange(params.row.id, e.target.checked ? 'activo' : 'inactivo')}
                color={isActive ? 'success' : 'default'}
                size="small"
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: isActive ? 'success.main' : 'text.secondary',
                  fontWeight: 500,
                  minWidth: 70,
                  textAlign: 'center'
                }}
              >
                {isActive ? 'Activo' : 'Inactivo'}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 250,
      headerAlign: 'left',
      align: 'left',
      cellClassName: 'left-aligned-cell',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <IconButton 
            onClick={() => {
              setNotificationToEdit(params.row);
              setEditDialogOpen(true);
            }}
            size="small"
            sx={{ 
              ml: 1,
              color: 'rgba(0, 0, 0, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                color: 'rgba(0, 0, 0, 0.9)',
              },
            }}
            title="Editar"
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            onClick={() => handleDeleteClick(params.row)}
            size="small"
            sx={{ 
              ml: 1,
              color: 'rgba(0, 0, 0, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                color: 'rgba(0, 0, 0, 0.9)',
              },
            }}
            title="Eliminar"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, bgcolor: 'background.paper', p: 3, borderRadius: 2, boxShadow: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box display="flex" alignItems="center">
            <NotificationsIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Notificaciones a Consumidores
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              color: '#C8102E',
              backgroundColor: 'white',
              borderColor: '#C8102E',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#C8102E',
                color: 'white',
                borderColor: '#C8102E',
              },
            }}
          >
            Nueva Notificación
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar notificaciones..."
          value={searchText}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchText && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} edge="end">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ 
        height: '70vh',
        width: '100%',
        '& .MuiDataGrid-row': {
          marginBottom: 1,
          '&:last-child': {
            marginBottom: 0,
          },
        },
        '& .left-aligned-cell': {
          justifyContent: 'flex-start',
          paddingLeft: '24px',
        },
        '& .MuiDataGrid-root': {
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            borderRight: 'none',
            padding: '8px 16px',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: 'none',
            padding: '8px 16px',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
          '& .MuiDataGrid-row': {
            backgroundColor: 'white',
            borderRadius: 1,
            marginBottom: 1,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
              },
            },
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
        },
      }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[5, 10, 25]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          disableColumnMenu={false}
          disableColumnFilter={false}
          disableColumnSelector={false}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
          localeText={{
            noRowsLabel: 'No hay notificaciones para mostrar',
            footerRowSelected: (count) =>
              count !== 1
                ? `${count.toLocaleString()} notificaciones seleccionadas`
                : '1 notificación seleccionada',
          }}
        />
      </Box>

// ...
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirmar eliminación
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Está seguro que desea eliminar la notificación "{notificationToDelete?.title}"?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error"
            variant="contained"
            autoFocus
            disabled={isDeleting}
            sx={{
              '&:hover': {
                backgroundColor: '#d32f2f',
              },
            }}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddNotificationDialog 
        open={addDialogOpen} 
        onClose={(created = false) => {
          setAddDialogOpen(false);
          if (created) {
            fetchNotifications();
          }
        }} 
      />

      <EditNotificationDialog
        open={editDialogOpen}
        notification={notificationToEdit}
        onClose={(updated = false) => {
          setEditDialogOpen(false);
          setNotificationToEdit(null);
          if (updated) {
            fetchNotifications();
          }
        }}
      />
    </Container>
  );
};

export default NotificationsPage;
